import {
    AlertCircle, Calendar, ChevronRight, ClipboardList, FlaskConical,
    Info, RefreshCw, Search, X, Clock, Users, Building2, Activity,
    PieChart, CheckCircle2, CircleDot, Timer, LineChart, Gauge, Filter,
    Layers, User, FileText
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { formattedDateLong } from "../../components/Date";
import { BASE_URL } from "../../config/axios";
import axios from "axios";
import { handleErrorToast } from "../../components/CustomFunctions";
import { Modal } from "react-bootstrap";

const statusColors = {
    Open: '#f59e0b',
    Ongoing: '#3b82f6',
    Closed: '#22c55e',
};

const STAT_MODAL_CONFIG = {
    total: { title: 'All Projects', icon: FlaskConical, accent: '#6366f1', accent2: '#8b5cf6' },
    open: { title: 'Open Projects', icon: Info, accent: '#f59e0b', accent2: '#f97316' },
    ongoing: { title: 'Ongoing Projects', icon: RefreshCw, accent: '#3b82f6', accent2: '#6366f1' },
    closed: { title: 'Closed Projects', icon: ClipboardList, accent: '#22c55e', accent2: '#10b981' },
    duration: { title: 'Projects by Duration', icon: Calendar, accent: '#8b5cf6', accent2: '#a855f7' },
};

const LANES = ['Open', 'Ongoing', 'Closed'];

const ProjectDashboard = () => {
    const [projects, setProjects] = useState([]);
    const [buffer, setBuffering] = useState(false);
    const [activeStatModal, setActiveStatModal] = useState(null);
    const [modalSearch, setModalSearch] = useState('');
    const [quickView, setQuickView] = useState(null);
    const [trendView, setTrendView] = useState('area'); // 'area' | 'stacked'
    const [hoverBubble, setHoverBubble] = useState(null);

    const getProjects = async () => {
        setBuffering(true);
        try {
            const res = await axios.get(`${BASE_URL}/labmonitoring/labjob/`);
            const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
            setProjects(data);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setBuffering(false);
        }
    };

    useEffect(() => { getProjects(); }, []);

    /* ====================== STATS ====================== */
    const stats = useMemo(() => {
        const total = projects.length;
        const open = projects.filter((p) => p.project_status === 'Open').length;
        const ongoing = projects.filter((p) => p.project_status === 'Ongoing').length;
        const closed = projects.filter((p) => p.project_status === 'Closed').length;
        const durations = projects.map((p) => Number(p.total_duration)).filter((d) => !isNaN(d) && d > 0);
        const avgDuration = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
        const completionRate = total > 0 ? Math.round((closed / total) * 100) : 0;
        return { total, open, ongoing, closed, avgDuration, completionRate };
    }, [projects]);

    /* ====================== STATUS BREAKDOWN ====================== */
    const statusBreakdown = useMemo(() => {
        const { total, open, ongoing, closed } = stats;
        if (total === 0) return [];
        return [
            { key: 'open', label: 'Open', value: open, pct: (open / total) * 100, color: statusColors.Open },
            { key: 'ongoing', label: 'Ongoing', value: ongoing, pct: (ongoing / total) * 100, color: statusColors.Ongoing },
            { key: 'closed', label: 'Closed', value: closed, pct: (closed / total) * 100, color: statusColors.Closed },
        ].filter((s) => s.value > 0);
    }, [stats]);

    const donutGradient = useMemo(() => {
        let cursor = 0;
        const segments = statusBreakdown.map((s) => {
            const start = cursor;
            const end = cursor + s.pct;
            cursor = end;
            return `${s.color} ${start}% ${end}%`;
        });
        return segments.length ? `conic-gradient(from -90deg, ${segments.join(', ')})` : '#e5e7eb';
    }, [statusBreakdown]);

    /* ====================== MONTHLY TREND ====================== */
    const monthlyTrend = useMemo(() => {
        const map = {};
        projects.forEach((p) => {
            if (!p.project_confirmation_date) return;
            const d = new Date(p.project_confirmation_date);
            if (isNaN(d.getTime())) return;
            const key = `${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}`;
            if (!map[key]) map[key] = { open: 0, ongoing: 0, closed: 0, total: 0 };
            const st = p.project_status || 'Open';
            if (st === 'Open') map[key].open += 1;
            else if (st === 'Ongoing') map[key].ongoing += 1;
            else if (st === 'Closed') map[key].closed += 1;
            map[key].total += 1;
        });
        const entries = Object.entries(map)
            .map(([label, counts]) => ({ label, ...counts, sortKey: new Date(label).getTime() }))
            .sort((a, b) => a.sortKey - b.sortKey)
            .slice(-8);
        const max = Math.max(1, ...entries.map((e) => e.total));
        return { entries, max };
    }, [projects]);

    const areaChartData = useMemo(() => {
        const entries = monthlyTrend.entries;
        if (!entries.length) return null;
        const W = 600, H = 220, PAD = { t: 28, r: 24, b: 32, l: 24 };
        const innerW = W - PAD.l - PAD.r;
        const innerH = H - PAD.t - PAD.b;
        const max = Math.max(1, ...entries.map((e) => e.total));
        const stepX = entries.length > 1 ? innerW / (entries.length - 1) : 0;
        const startX = entries.length > 1 ? PAD.l : PAD.l + innerW / 2;

        const points = entries.map((e, i) => ({
            x: startX + i * stepX,
            y: PAD.t + innerH - (e.total / max) * innerH,
            value: e.total,
            label: e.label,
        }));

        const linePath = points
            .map((p, i, arr) => {
                if (i === 0) return `M ${p.x} ${p.y}`;
                const prev = arr[i - 1];
                const cx = (prev.x + p.x) / 2;
                return `C ${cx} ${prev.y}, ${cx} ${p.y}, ${p.x} ${p.y}`;
            })
            .join(' ');
        const areaPath = `${linePath} L ${points[points.length - 1].x} ${PAD.t + innerH} L ${points[0].x} ${PAD.t + innerH} Z`;
        return { W, H, PAD, innerW, innerH, max, points, linePath, areaPath };
    }, [monthlyTrend]);

    const stackedData = useMemo(() => monthlyTrend.entries, [monthlyTrend]);

    /* ====================== RESPONSIBLE BREAKDOWN ====================== */
    const responsibleBreakdown = useMemo(() => {
        const map = {};
        projects.forEach((p) => {
            const r = p.responsible_person || 'Unassigned';
            map[r] = (map[r] || 0) + 1;
        });
        const entries = Object.entries(map)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 6);
        const max = Math.max(1, ...entries.map((e) => e.count));
        return { entries, max };
    }, [projects]);

    /* ====================== FUNNEL ====================== */
    const funnelData = useMemo(() => {
        const total = projects.length;
        return [
            { label: 'Created', value: total, color: '#6366f1' },
            { label: 'Scoped', value: projects.filter((p) => p.project_scope).length, color: '#7c6df2' },
            { label: 'Tested', value: projects.filter((p) => p.test_performed).length, color: '#8b5cf6' },
            { label: 'Surveyed', value: projects.filter((p) => p.survey_date).length, color: '#a855f7' },
            { label: 'Drafted', value: projects.filter((p) => p.draft_report_submission_date).length, color: '#c04fe0' },
            { label: 'Finalized', value: projects.filter((p) => p.final_report_submission_date).length, color: '#e0479e' },
            { label: 'Closed', value: projects.filter((p) => p.project_status === 'Closed').length, color: '#22c55e' },
        ];
    }, [projects]);

    /* ====================== BUBBLE (Duration vs Status) ====================== */
    const bubbleChart = useMemo(() => {
        const valid = projects.filter((p) => Number(p.total_duration) > 0 && LANES.includes(p.project_status));
        if (!valid.length) return null;
        const maxDur = Math.max(...valid.map((p) => Number(p.total_duration)));
        const xMax = Math.max(10, Math.ceil(maxDur / 5) * 5);
        const X0 = 84, X1 = 580, LANE_H = 62, GAP = 8, TOP = 10;
        const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(xMax * f));

        const bubbles = valid.slice(0, 80).map((p, i) => {
            const dur = Number(p.total_duration);
            const lane = LANES.indexOf(p.project_status);
            const jitter = (((i * 37) % 100) / 100 - 0.5) * 26; // deterministic spread inside lane
            return {
                id: p.id ?? i,
                project: p,
                lane,
                dur,
                x: X0 + (dur / xMax) * (X1 - X0),
                y: TOP + lane * (LANE_H + GAP) + LANE_H / 2 + jitter,
                r: 6 + Math.sqrt(dur / xMax) * 9,
                color: statusColors[p.project_status],
            };
        });

        // avg duration per lane
        const laneAvg = LANES.map((l) => {
            const arr = valid.filter((p) => p.project_status === l).map((p) => Number(p.total_duration));
            return { label: l, count: arr.length, avg: arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0 };
        });

        const H = TOP * 2 + LANES.length * LANE_H + (LANES.length - 1) * GAP + 22;
        return { bubbles, xMax, X0, X1, LANE_H, GAP, TOP, ticks, H, laneAvg, W: 600 };
    }, [projects]);

    /* ====================== RECENT & DEADLINES ====================== */
    const recentActivity = useMemo(() => {
        return [...projects]
            .filter((p) => p.project_confirmation_date)
            .sort((a, b) => new Date(b.project_confirmation_date) - new Date(a.project_confirmation_date))
            .slice(0, 5);
    }, [projects]);

    const upcomingDeadlines = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return [...projects]
            .filter((p) => p.final_report_submission_date && p.project_status !== 'Closed')
            .map((p) => ({ ...p, _dueDate: new Date(p.final_report_submission_date) }))
            .filter((p) => !isNaN(p._dueDate.getTime()))
            .sort((a, b) => a._dueDate - b._dueDate)
            .slice(0, 6)
            .map((p) => {
                const diffDays = Math.round((p._dueDate - today) / (1000 * 60 * 60 * 24));
                return { ...p, _overdue: diffDays < 0, _daysLeft: diffDays };
            });
    }, [projects]);

    /* ====================== MODAL ====================== */
    const openStatModal = (key) => { setModalSearch(''); setActiveStatModal(key); };
    const closeStatModal = () => { setActiveStatModal(null); setModalSearch(''); };

    const statModalData = useMemo(() => {
        if (!activeStatModal) return [];
        let data = projects;
        if (activeStatModal === 'open') data = projects.filter((p) => p.project_status === 'Open');
        if (activeStatModal === 'ongoing') data = projects.filter((p) => p.project_status === 'Ongoing');
        if (activeStatModal === 'closed') data = projects.filter((p) => p.project_status === 'Closed');
        if (activeStatModal === 'duration') {
            data = projects.filter((p) => Number(p.total_duration) > 0)
                .sort((a, b) => Number(b.total_duration) - Number(a.total_duration));
        }
        if (modalSearch.trim()) {
            const q = modalSearch.toLowerCase();
            data = data.filter((p) =>
                p.project_code?.toString().toLowerCase().includes(q) ||
                p.project_name?.toString().toLowerCase().includes(q) ||
                p.client_detail?.toString().toLowerCase().includes(q) ||
                p.responsible_person?.toString().toLowerCase().includes(q)
            );
        }
        return data;
    }, [activeStatModal, projects, modalSearch]);

    const modalAvgDuration = useMemo(() => {
        const d = statModalData.map((p) => Number(p.total_duration)).filter((x) => !isNaN(x) && x > 0);
        return d.length ? Math.round(d.reduce((a, b) => a + b, 0) / d.length) : 0;
    }, [statModalData]);

    const activeConfig = activeStatModal ? STAT_MODAL_CONFIG[activeStatModal] : null;
    const ActiveIcon = activeConfig?.icon;

    /* ====================== GAUGE ====================== */
    const gaugeArc = useMemo(() => {
        const size = 140, stroke = 14;
        const r = (size - stroke) / 2;
        const cx = size / 2, cy = size / 2;
        const startAngle = 135, endAngle = 405;
        const totalAngle = endAngle - startAngle;
        const valueAngle = startAngle + (stats.completionRate / 100) * totalAngle;
        const polar = (ang) => {
            const rad = (ang * Math.PI) / 180;
            return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
        };
        const bgStart = polar(startAngle), bgEnd = polar(endAngle), valEnd = polar(valueAngle);
        const arcLen = r * ((totalAngle * Math.PI) / 180);
        return {
            cx, cy, arcLen,
            offset: arcLen * (1 - stats.completionRate / 100),
            bgPath: `M ${bgStart.x} ${bgStart.y} A ${r} ${r} 0 1 1 ${bgEnd.x} ${bgEnd.y}`,
            valPath: `M ${bgStart.x} ${bgStart.y} A ${r} ${r} 0 ${valueAngle - startAngle > 180 ? 1 : 0} 1 ${valEnd.x} ${valEnd.y}`,
        };
    }, [stats.completionRate]);

    const statCards = [
        { key: 'total', label: 'Total Projects', value: stats.total, icon: FlaskConical, color: '#6366f1', soft: 'rgba(99,102,241,0.12)', hint: 'View all', pct: 100 },
        { key: 'open', label: 'Open', value: stats.open, icon: CircleDot, color: statusColors.Open, soft: 'rgba(245,158,11,0.12)', hint: 'View list', pct: stats.total ? (stats.open / stats.total) * 100 : 0 },
        { key: 'ongoing', label: 'Ongoing', value: stats.ongoing, icon: Activity, color: statusColors.Ongoing, soft: 'rgba(59,130,246,0.12)', hint: 'View list', pct: stats.total ? (stats.ongoing / stats.total) * 100 : 0 },
        { key: 'closed', label: 'Closed', value: stats.closed, icon: CheckCircle2, color: statusColors.Closed, soft: 'rgba(34,197,94,0.12)', hint: 'View list', pct: stats.total ? (stats.closed / stats.total) * 100 : 0 },
        { key: 'duration', label: 'Avg Duration', value: stats.avgDuration, unit: 'days', icon: Timer, color: '#8b5cf6', soft: 'rgba(139,92,246,0.12)', hint: 'View breakdown', pct: 0 },
    ];

    const StatusPill = ({ status }) => {
        const c = statusColors[status] || '#94a3b8';
        return (
            <span className="pd-pill" style={{ '--c': c }}>
                <i />{status || '-'}
            </span>
        );
    };

    /* ====================== RENDER ====================== */
    return (
        <div className="progress-modern-container">
            <style>{`
                @keyframes pdFadeUp { from { opacity: 0; transform: translateY(14px);} to { opacity:1; transform: translateY(0);} }
                @keyframes pdScaleIn { from { opacity: 0; transform: scale(0.6);} to { opacity:1; transform: scale(1);} }
                @keyframes pdPulse { 0%,100% { opacity: 1;} 50% { opacity: 0.45;} }
                @keyframes pdShimmer { 0% { background-position: -200% 0;} 100% { background-position: 200% 0;} }
                @keyframes pdGrowBar { from { transform: scaleY(0);} to { transform: scaleY(1);} }
                @keyframes pdGrowX { from { transform: scaleX(0);} to { transform: scaleX(1);} }
                @keyframes pdDrawLine { from { stroke-dashoffset: var(--dash);} to { stroke-dashoffset: 0;} }
                @keyframes pdRiseArea { from { opacity: 0;} to { opacity: 1;} }
                @keyframes pdGaugeFill { from { stroke-dashoffset: var(--gauge-dash);} to { stroke-dashoffset: var(--gauge-offset);} }
                @keyframes pdModalIn { from { opacity: 0; transform: translateY(12px) scale(0.98);} to { opacity: 1; transform: none;} }

                /* ============= STATS ============= */
                .pd-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 16px; margin-bottom: 18px; }
                .pd-stat-card {
                    background: #fff; border: 1px solid #eef2f7; border-radius: 18px;
                    padding: 18px 18px 16px; display: flex; flex-direction: column; gap: 6px;
                    position: relative; overflow: hidden; cursor: pointer; text-align: left;
                    transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
                    animation: pdFadeUp .5s ease both; isolation: isolate;
                }
                .pd-stat-card::before {
                    content: ''; position: absolute; inset: 0; z-index: -1;
                    background: radial-gradient(circle at 100% 0%, var(--c-soft), transparent 60%);
                }
                .pd-stat-card:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -14px rgba(15,23,42,.2); border-color: var(--c); }
                .pd-stat-top { display: flex; align-items: center; justify-content: space-between; }
                .pd-stat-icon-wrap { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: var(--c-soft); color: var(--c); transition: transform .3s ease; }
                .pd-stat-card:hover .pd-stat-icon-wrap { transform: scale(1.08) rotate(-5deg); }
                .pd-stat-share { font-size: 11px; font-weight: 700; color: var(--c); background: var(--c-soft); padding: 3px 8px; border-radius: 999px; }
                .pd-stat-label { font-size: 12.5px; color: #64748b; font-weight: 600; margin-top: 6px; }
                .pd-stat-value { font-size: 32px; font-weight: 800; color: #0f172a; line-height: 1.05; display: flex; align-items: baseline; gap: 5px; letter-spacing: -0.8px; }
                .pd-stat-value small { font-size: 13px; font-weight: 600; color: #94a3b8; letter-spacing: 0; }
                .pd-stat-track { height: 5px; background: #f1f5f9; border-radius: 999px; overflow: hidden; margin-top: 6px; }
                .pd-stat-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, var(--c), color-mix(in srgb, var(--c) 60%, #fff)); transform-origin: left; animation: pdGrowX .9s ease .2s both; }
                .pd-stat-hint { font-size: 11.5px; color: #94a3b8; display: flex; align-items: center; gap: 3px; margin-top: 4px; transition: color .2s ease, gap .2s ease; }
                .pd-stat-card:hover .pd-stat-hint { color: var(--c); gap: 6px; }

                /* ============= PANELS ============= */
                .pd-panel { background: #fff; border: 1px solid #eef2f7; border-radius: 18px; padding: 20px; animation: pdFadeUp .55s ease both; transition: box-shadow .25s ease, border-color .25s ease; position: relative; min-width: 0; }
                .pd-panel:hover { box-shadow: 0 12px 28px -16px rgba(15,23,42,.14); border-color: #e2e8f0; }
                .pd-panel-title { font-size: 14px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 9px; margin-bottom: 16px; letter-spacing: -0.2px; }
                .pd-panel-title-icon { width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #eef2ff, #e0e7ff); color: #4f46e5; flex-shrink: 0; }
                .pd-panel-meta { margin-left: auto; font-size: 11.5px; color: #94a3b8; font-weight: 500; }
                .pd-panel-actions { margin-left: auto; display: flex; gap: 3px; background: #f1f5f9; padding: 3px; border-radius: 9px; }
                .pd-panel-action-btn { font-size: 11px; font-weight: 600; padding: 5px 10px; border-radius: 7px; border: none; background: none; color: #64748b; cursor: pointer; transition: all .2s ease; }
                .pd-panel-action-btn.active { background: #fff; color: #4f46e5; box-shadow: 0 1px 3px rgba(0,0,0,.08); }

                /* ============= GRIDS ============= */
                .pd-row { display: grid; gap: 16px; margin-bottom: 16px; }
                .pd-row-top { grid-template-columns: 1.5fr 1.1fr 0.9fr; }
                .pd-row-mid { grid-template-columns: 1.6fr 1fr; }
                .pd-row-bottom { grid-template-columns: repeat(3, 1fr); margin-bottom: 0; }

                /* ============= SVG CHARTS ============= */
                .pd-svg-chart { width: 100%; height: auto; display: block; overflow: visible; }
                .pd-grid-line { stroke: #f1f5f9; stroke-width: 1; stroke-dasharray: 3 3; }
                .pd-axis-line { stroke: #e2e8f0; stroke-width: 1; }
                .pd-axis-label { fill: #94a3b8; font-size: 10.5px; font-weight: 600; }
                .pd-value-label { fill: #4f46e5; font-size: 11px; font-weight: 800; }
                .pd-line-path { fill: none; stroke: url(#pdLineGrad); stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: var(--dash); stroke-dashoffset: var(--dash); animation: pdDrawLine 1.4s ease forwards; }
                .pd-area-path { fill: url(#pdAreaGrad); opacity: 0; animation: pdRiseArea .9s ease .5s forwards; }
                .pd-point { fill: #fff; stroke: #6366f1; stroke-width: 2.5; transition: r .2s ease; cursor: pointer; }
                .pd-point:hover { r: 7; }

                /* ============= STACKED ============= */
                .pd-stack-bars { display: flex; align-items: flex-end; gap: 10px; height: 200px; padding-top: 24px; border-bottom: 1px dashed #e2e8f0; }
                .pd-stack-col { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; justify-content: flex-end; gap: 8px; position: relative; }
                .pd-stack-bar { width: 70%; display: flex; flex-direction: column-reverse; border-radius: 8px 8px 3px 3px; overflow: hidden; transform-origin: bottom; animation: pdGrowBar .7s cubic-bezier(.34,1.56,.64,1) both; }
                .pd-stack-label { font-size: 10.5px; color: #64748b; font-weight: 600; white-space: nowrap; transform: translateY(20px); position: absolute; bottom: 0; }
                .pd-stack-tooltip { position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%); background: #0f172a; color: #fff; padding: 5px 9px; border-radius: 8px; font-size: 11px; font-weight: 600; white-space: nowrap; opacity: 0; pointer-events: none; transition: opacity .2s ease; z-index: 10; }
                .pd-stack-col:hover .pd-stack-tooltip { opacity: 1; }
                .pd-legend-row { display: flex; gap: 14px; margin-top: 30px; font-size: 11.5px; color: #64748b; flex-wrap: wrap; }
                .pd-legend-row span { display: flex; align-items: center; gap: 5px; font-weight: 600; }
                .pd-legend-row i { width: 9px; height: 9px; border-radius: 3px; display: inline-block; }

                /* ============= GAUGE ============= */
                .pd-gauge-wrap { display: flex; justify-content: center; }
                .pd-gauge-svg { width: 100%; max-width: 170px; height: auto; }
                .pd-gauge-bg { fill: none; stroke: #f1f5f9; stroke-width: 14; stroke-linecap: round; }
                .pd-gauge-val { fill: none; stroke: url(#pdGaugeGrad); stroke-width: 14; stroke-linecap: round; stroke-dasharray: var(--gauge-dash); stroke-dashoffset: var(--gauge-dash); animation: pdGaugeFill 1.3s cubic-bezier(.4,0,.2,1) .2s forwards; }
                .pd-gauge-split { display: flex; justify-content: space-around; margin-top: 14px; padding-top: 14px; border-top: 1px dashed #e2e8f0; }
                .pd-gauge-split div { text-align: center; }
                .pd-gauge-split strong { display: block; font-size: 19px; font-weight: 800; color: #0f172a; }
                .pd-gauge-split span { font-size: 11.5px; color: #94a3b8; font-weight: 600; }

                /* ============= DONUT ============= */
                .pd-donut-wrap { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: center; }
                .pd-donut { width: 128px; height: 128px; border-radius: 50%; flex-shrink: 0; position: relative; animation: pdScaleIn .6s ease both; }
                .pd-donut::after { content: ''; position: absolute; inset: 20px; background: #fff; border-radius: 50%; }
                .pd-donut-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 2; pointer-events: none; }
                .pd-donut-center strong { font-size: 22px; font-weight: 800; color: #0f172a; line-height: 1; }
                .pd-donut-center span { font-size: 10.5px; color: #94a3b8; margin-top: 3px; font-weight: 600; }
                .pd-legend { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 140px; }
                .pd-legend-item { display: flex; align-items: center; gap: 9px; font-size: 13px; color: #334155; background: none; border: none; cursor: pointer; padding: 8px 10px; border-radius: 10px; transition: all .2s ease; text-align: left; width: 100%; font-weight: 500; }
                .pd-legend-item:hover { background: #f8fafc; }
                .pd-legend-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
                .pd-legend-pct { margin-left: auto; font-size: 11.5px; font-weight: 700; color: #64748b; background: #f1f5f9; padding: 2px 7px; border-radius: 6px; }

                /* ============= FUNNEL ============= */
                .pd-funnel { display: flex; flex-direction: column; gap: 9px; }
                .pd-funnel-step { display: grid; grid-template-columns: 64px 1fr 38px; align-items: center; gap: 10px; }
                .pd-funnel-label { font-size: 12px; color: #475569; font-weight: 600; }
                .pd-funnel-track { height: 12px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
                .pd-funnel-fill { height: 100%; border-radius: 999px; transform-origin: left; animation: pdGrowX .8s ease both; }
                .pd-funnel-count { font-size: 12.5px; font-weight: 800; color: #0f172a; text-align: right; }

                /* ============= BUBBLE ============= */
                .pd-bubble-wrap { position: relative; }
                .pd-bubble { cursor: pointer; transition: filter .2s ease, stroke-width .2s ease; transform-box: fill-box; transform-origin: center; animation: pdScaleIn .55s cubic-bezier(.34,1.56,.64,1) both; }
                .pd-bubble:hover { filter: brightness(1.08) drop-shadow(0 4px 8px rgba(15,23,42,.25)); stroke-width: 3; }
                .pd-lane-label { fill: #475569; font-size: 11px; font-weight: 700; }
                .pd-bubble-tip { position: absolute; transform: translate(-50%, calc(-100% - 12px)); background: #0f172a; color: #fff; border-radius: 10px; padding: 8px 12px; font-size: 11.5px; pointer-events: none; z-index: 5; min-width: 150px; box-shadow: 0 10px 24px -8px rgba(15,23,42,.4); }
                .pd-bubble-tip::after { content: ''; position: absolute; left: 50%; bottom: -5px; width: 10px; height: 10px; background: #0f172a; transform: translateX(-50%) rotate(45deg); }
                .pd-bubble-tip strong { display: block; font-size: 12px; margin-bottom: 2px; }
                .pd-bubble-tip span { color: #cbd5e1; display: block; }
                .pd-bubble-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 12px; }
                .pd-bubble-summary div { background: #f8fafc; border-radius: 12px; padding: 9px 12px; border-left: 3px solid var(--c); }
                .pd-bubble-summary strong { display: block; font-size: 16px; font-weight: 800; color: #0f172a; }
                .pd-bubble-summary span { font-size: 11px; color: #64748b; font-weight: 600; }

                /* ============= HBAR ============= */
                .pd-hbar-list { display: flex; flex-direction: column; gap: 14px; }
                .pd-hbar-head { display: flex; justify-content: space-between; font-size: 12.5px; margin-bottom: 6px; }
                .pd-hbar-name { color: #334155; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .pd-hbar-count { color: #0f172a; font-weight: 800; }
                .pd-hbar-track { height: 8px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
                .pd-hbar-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #6366f1, #a78bfa); transform-origin: left; animation: pdGrowX .8s ease both; }

                /* ============= LISTS ============= */
                .pd-list-item { display: flex; align-items: center; justify-content: space-between; padding: 11px 10px; border-radius: 10px; gap: 10px; cursor: pointer; transition: background .2s ease; }
                .pd-list-item:hover { background: #f8fafc; }
                .pd-list-item + .pd-list-item { border-top: 1px solid #f1f5f9; }
                .pd-list-body { min-width: 0; flex: 1; }
                .pd-list-main { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .pd-list-sub { font-size: 11.5px; color: #64748b; margin-top: 2px; display: flex; align-items: center; gap: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .pd-date-chip { font-size: 11px; font-weight: 600; color: #475569; background: #f1f5f9; padding: 4px 9px; border-radius: 999px; white-space: nowrap; flex-shrink: 0; }
                .pd-deadline-badge { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 999px; flex-shrink: 0; white-space: nowrap; }
                .pd-deadline-badge.overdue { background: #fee2e2; color: #dc2626; }
                .pd-deadline-badge.upcoming { background: #eef2ff; color: #4338ca; }
                .pd-deadline-badge.today { background: #fef3c7; color: #b45309; animation: pdPulse 1.6s ease-in-out infinite; }

                .pd-empty { font-size: 12.5px; color: #94a3b8; text-align: center; padding: 30px 0; display: flex; flex-direction: column; align-items: center; gap: 8px; }
                .pd-empty svg { opacity: .4; }

                /* ============= PILL ============= */
                .pd-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; padding: 4px 11px; border-radius: 999px; color: var(--c); background: color-mix(in srgb, var(--c) 12%, #fff); }
                .pd-pill i { width: 6px; height: 6px; border-radius: 50%; background: var(--c); }

                /* ============= MODALS ============= */
                .pd-modal .modal-content { border: none; border-radius: 20px; overflow: hidden; box-shadow: 0 30px 80px -20px rgba(15,23,42,.45); animation: pdModalIn .3s ease both; }
                .pd-modal-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 22px 26px; color: #fff; background: linear-gradient(135deg, var(--h1), var(--h2)); position: relative; overflow: hidden; border: none; }
                .pd-modal-head::after { content: ''; position: absolute; right: -40px; top: -60px; width: 200px; height: 200px; border-radius: 50%; background: rgba(255,255,255,.12); }
                .pd-modal-head-left { display: flex; align-items: center; gap: 14px; position: relative; z-index: 1; min-width: 0; }
                .pd-modal-head-icon { width: 46px; height: 46px; border-radius: 14px; background: rgba(255,255,255,.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; backdrop-filter: blur(4px); }
                .pd-modal-head h5 { margin: 0; font-size: 19px; font-weight: 800; color: #fff; letter-spacing: -0.3px; }
                .pd-modal-head p { margin: 2px 0 0; font-size: 12.5px; color: rgba(255,255,255,.85); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .pd-modal-close { position: relative; z-index: 1; width: 36px; height: 36px; border-radius: 10px; border: none; background: rgba(255,255,255,.18); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background .2s ease, transform .2s ease; flex-shrink: 0; }
                .pd-modal-close:hover { background: rgba(255,255,255,.32); transform: rotate(90deg); }
                .pd-modal-bodyx { padding: 20px 26px 24px; background: #fff; }
                .pd-modal-chips { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
                .pd-modal-chip { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 12px; padding: 8px 14px; font-size: 12px; color: #64748b; font-weight: 600; }
                .pd-modal-chip strong { color: #0f172a; font-size: 15px; font-weight: 800; margin-right: 4px; }
                .pd-modal-search { display: flex; align-items: center; gap: 10px; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px 14px; margin-bottom: 14px; transition: all .2s ease; }
                .pd-modal-search:focus-within { border-color: var(--h1); background: #fff; box-shadow: 0 0 0 3px color-mix(in srgb, var(--h1) 15%, transparent); }
                .pd-modal-search input { border: none; background: none; outline: none; width: 100%; font-size: 13.5px; color: #0f172a; }
                .pd-modal-search input::placeholder { color: #94a3b8; }
                .pd-modal-table-wrap { border: 1px solid #eef2f7; border-radius: 14px; overflow: hidden; }
                .pd-modal-scroll { overflow: auto; max-height: 440px; }
                .pd-modal-table { width: 100%; border-collapse: collapse; font-size: 13px; white-space: nowrap; }
                .pd-modal-table thead th { position: sticky; top: 0; background: #f8fafc; color: #64748b; font-weight: 700; text-align: left; padding: 12px 16px; border-bottom: 1px solid #e2e8f0; z-index: 1; font-size: 12px; }
                .pd-modal-table tbody td { padding: 12px 16px; color: #334155; border-bottom: 1px solid #f1f5f9; }
                .pd-modal-table tbody tr:last-child td { border-bottom: none; }
                .pd-modal-table tbody tr { transition: background .15s ease; cursor: pointer; }
                .pd-modal-table tbody tr:hover { background: color-mix(in srgb, var(--h1) 6%, #fff); }
                .pd-code { font-weight: 700; color: var(--h1); }
                .pd-dur-cell { display: flex; align-items: center; gap: 8px; min-width: 110px; }
                .pd-dur-bar { flex: 1; height: 5px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
                .pd-dur-bar i { display: block; height: 100%; background: linear-gradient(90deg, var(--h1), var(--h2)); border-radius: 999px; }

                /* Quick view */
                .pd-qv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
                .pd-qv-tile { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 12px; padding: 11px 14px; min-width: 0; }
                .pd-qv-tile.full { grid-column: 1 / -1; }
                .pd-qv-tile label { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #94a3b8; font-weight: 600; margin: 0 0 4px; }
                .pd-qv-tile div { font-size: 13.5px; font-weight: 600; color: #0f172a; word-break: break-word; }
                .pd-qv-remark { background: #fffbeb; border: 1px solid #fde68a; border-radius: 12px; padding: 10px 14px; font-size: 13px; color: #78350f; margin-top: 10px; }
                .pd-qv-remark b { display: block; font-size: 11px; color: #b45309; margin-bottom: 2px; }

                /* ============= SKELETON ============= */
                .pd-skeleton-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 16px; margin-bottom: 20px; }
                .pd-skeleton { height: 128px; border-radius: 18px; background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: pdShimmer 1.5s infinite; }

                @media (prefers-reduced-motion: reduce) { .pd-panel, .pd-stat-card, .pd-bubble, .pd-funnel-fill, .pd-hbar-fill { animation: none !important; } }
                @media (max-width: 1100px) {
                    .pd-row-top { grid-template-columns: 1fr 1fr; }
                    .pd-row-top > :first-child { grid-column: 1 / -1; }
                    .pd-row-mid, .pd-row-bottom { grid-template-columns: 1fr; }
                }
                @media (max-width: 700px) {
                    .pd-row-top { grid-template-columns: 1fr; }
                    .pd-qv-grid { grid-template-columns: 1fr; }
                    .pd-bubble-summary { grid-template-columns: 1fr; }
                }
            `}</style>

            {buffer ? (
                <div className="pd-skeleton-grid">
                    {[1, 2, 3, 4, 5].map((n) => <div className="pd-skeleton" key={n} />)}
                </div>
            ) : (
                <>
                    {/* ============== STAT CARDS ============== */}
                    <div className="pd-stats-grid">
                        {statCards.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <button
                                    key={c.key}
                                    className="pd-stat-card"
                                    style={{ '--c': c.color, '--c-soft': c.soft, animationDelay: `${0.05 * (i + 1)}s` }}
                                    onClick={() => openStatModal(c.key)}
                                >
                                    <div className="pd-stat-top">
                                        <div className="pd-stat-icon-wrap"><Icon size={18} /></div>
                                        {c.key !== 'total' && c.key !== 'duration' && (
                                            <span className="pd-stat-share">{Math.round(c.pct)}%</span>
                                        )}
                                    </div>
                                    <div className="pd-stat-label">{c.label}</div>
                                    <div className="pd-stat-value">{c.value}{c.unit && <small>{c.unit}</small>}</div>
                                    {c.key !== 'duration' && (
                                        <div className="pd-stat-track"><div className="pd-stat-fill" style={{ width: `${c.pct}%` }} /></div>
                                    )}
                                    <div className="pd-stat-hint">{c.hint} <ChevronRight size={11} /></div>
                                </button>
                            );
                        })}
                    </div>

                    {/* ============== ROW 1: TREND + DONUT + GAUGE ============== */}
                    <div className="pd-row pd-row-top">
                        <div className="pd-panel" style={{ animationDelay: '0.3s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon"><LineChart size={15} /></div>
                                Monthly Trend
                                <div className="pd-panel-actions">
                                    <button className={`pd-panel-action-btn ${trendView === 'area' ? 'active' : ''}`} onClick={() => setTrendView('area')}>Area</button>
                                    <button className={`pd-panel-action-btn ${trendView === 'stacked' ? 'active' : ''}`} onClick={() => setTrendView('stacked')}>Stacked</button>
                                </div>
                            </div>
                            {areaChartData && trendView === 'area' ? (
                                <svg className="pd-svg-chart" viewBox={`0 0 ${areaChartData.W} ${areaChartData.H}`}>
                                    <defs>
                                        <linearGradient id="pdAreaGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                                            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                                        </linearGradient>
                                        <linearGradient id="pdLineGrad" x1="0" y1="0" x2="1" y2="0">
                                            <stop offset="0%" stopColor="#6366f1" />
                                            <stop offset="100%" stopColor="#8b5cf6" />
                                        </linearGradient>
                                    </defs>
                                    {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
                                        const y = areaChartData.PAD.t + areaChartData.innerH * (1 - f);
                                        return <line key={i} className="pd-grid-line" x1={areaChartData.PAD.l} x2={areaChartData.W - areaChartData.PAD.r} y1={y} y2={y} />;
                                    })}
                                    <line className="pd-axis-line" x1={areaChartData.PAD.l} x2={areaChartData.W - areaChartData.PAD.r} y1={areaChartData.H - areaChartData.PAD.b} y2={areaChartData.H - areaChartData.PAD.b} />
                                    <path className="pd-area-path" d={areaChartData.areaPath} />
                                    <path className="pd-line-path" d={areaChartData.linePath} style={{ '--dash': 1400, strokeDasharray: 1400 }} />
                                    {areaChartData.points.map((p, i) => (
                                        <g key={i}>
                                            <circle className="pd-point" cx={p.x} cy={p.y} r={5}><title>{p.label}: {p.value}</title></circle>
                                            <text className="pd-value-label" x={p.x} y={p.y - 12} textAnchor="middle">{p.value}</text>
                                            <text className="pd-axis-label" x={p.x} y={areaChartData.H - 10} textAnchor="middle">{p.label}</text>
                                        </g>
                                    ))}
                                </svg>
                            ) : stackedData.length > 0 ? (
                                <>
                                    <div className="pd-stack-bars">
                                        {stackedData.map((s, i) => (
                                            <div className="pd-stack-col" key={s.label}>
                                                <div className="pd-stack-tooltip" style={{ bottom: `calc(${(s.total / monthlyTrend.max) * 100}% + 4px)` }}>
                                                    {s.total} total · {s.open}O / {s.ongoing}P / {s.closed}C
                                                </div>
                                                <div className="pd-stack-bar" style={{ height: `${(s.total / monthlyTrend.max) * 100}%`, animationDelay: `${0.1 + i * 0.06}s` }}>
                                                    {s.closed > 0 && <div style={{ flex: s.closed, background: statusColors.Closed }} />}
                                                    {s.ongoing > 0 && <div style={{ flex: s.ongoing, background: statusColors.Ongoing }} />}
                                                    {s.open > 0 && <div style={{ flex: s.open, background: statusColors.Open }} />}
                                                </div>
                                                <span className="pd-stack-label">{s.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="pd-legend-row">
                                        {LANES.map((l) => <span key={l}><i style={{ background: statusColors[l] }} />{l}</span>)}
                                    </div>
                                </>
                            ) : (
                                <div className="pd-empty"><LineChart size={28} /><span>No data yet</span></div>
                            )}
                        </div>

                        <div className="pd-panel" style={{ animationDelay: '0.35s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon"><PieChart size={15} /></div>
                                Status Split
                            </div>
                            {stats.total > 0 ? (
                                <div className="pd-donut-wrap">
                                    <div className="pd-donut" style={{ background: donutGradient }}>
                                        <div className="pd-donut-center"><strong>{stats.total}</strong><span>Total</span></div>
                                    </div>
                                    <div className="pd-legend">
                                        {statusBreakdown.map((s) => (
                                            <button className="pd-legend-item" key={s.label} onClick={() => openStatModal(s.key)}>
                                                <span className="pd-legend-dot" style={{ background: s.color }} />
                                                <span>{s.label}</span>
                                                <span className="pd-legend-pct">{Math.round(s.pct)}%</span>
                                                <strong style={{ minWidth: 20, textAlign: 'right' }}>{s.value}</strong>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : <div className="pd-empty"><PieChart size={28} /><span>No data yet</span></div>}
                        </div>

                        <div className="pd-panel" style={{ animationDelay: '0.4s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon"><Gauge size={15} /></div>
                                Completion Rate
                            </div>
                            <div className="pd-gauge-wrap">
                                <svg className="pd-gauge-svg" viewBox="0 0 140 140">
                                    <defs>
                                        <linearGradient id="pdGaugeGrad" x1="0" y1="0" x2="1" y2="1">
                                            <stop offset="0%" stopColor="#22c55e" />
                                            <stop offset="100%" stopColor="#4ade80" />
                                        </linearGradient>
                                    </defs>
                                    <path className="pd-gauge-bg" d={gaugeArc.bgPath} />
                                    {stats.completionRate > 0 && (
                                        <path
                                            className="pd-gauge-val"
                                            d={gaugeArc.valPath}
                                            style={{ '--gauge-dash': gaugeArc.arcLen, '--gauge-offset': gaugeArc.offset }}
                                        />
                                    )}
                                    <text x="70" y="70" textAnchor="middle" fontSize="26" fontWeight="800" fill="#0f172a">{stats.completionRate}%</text>
                                    <text x="70" y="88" textAnchor="middle" fontSize="10" fontWeight="600" fill="#94a3b8">Closed</text>
                                </svg>
                            </div>
                            <div className="pd-gauge-split">
                                <div><strong>{stats.closed}</strong><span>Closed</span></div>
                                <div><strong>{stats.open + stats.ongoing}</strong><span>Active</span></div>
                            </div>
                        </div>
                    </div>

                    {/* ============== ROW 2: BUBBLE + FUNNEL ============== */}
                    <div className="pd-row pd-row-mid">
                        <div className="pd-panel" style={{ animationDelay: '0.45s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon"><Layers size={15} /></div>
                                Duration vs Status
                                <span className="pd-panel-meta">{bubbleChart ? `${bubbleChart.bubbles.length} projects` : ''}</span>
                            </div>
                            {bubbleChart ? (
                                <>
                                    <div className="pd-bubble-wrap" onMouseLeave={() => setHoverBubble(null)}>
                                        <svg className="pd-svg-chart" viewBox={`0 0 ${bubbleChart.W} ${bubbleChart.H}`}>
                                            {/* Lanes */}
                                            {LANES.map((l, i) => {
                                                const y = bubbleChart.TOP + i * (bubbleChart.LANE_H + bubbleChart.GAP);
                                                return (
                                                    <g key={l}>
                                                        <rect x={bubbleChart.X0 - 8} y={y} width={bubbleChart.X1 - bubbleChart.X0 + 16} height={bubbleChart.LANE_H} rx="12" fill={statusColors[l]} fillOpacity="0.07" />
                                                        <circle cx="14" cy={y + bubbleChart.LANE_H / 2} r="4" fill={statusColors[l]} />
                                                        <text className="pd-lane-label" x="26" y={y + bubbleChart.LANE_H / 2} dominantBaseline="middle">{l}</text>
                                                    </g>
                                                );
                                            })}
                                            {/* X grid */}
                                            {bubbleChart.ticks.map((t) => {
                                                const x = bubbleChart.X0 + (t / bubbleChart.xMax) * (bubbleChart.X1 - bubbleChart.X0);
                                                return (
                                                    <g key={t}>
                                                        <line className="pd-grid-line" x1={x} y1={bubbleChart.TOP} x2={x} y2={bubbleChart.H - 22} />
                                                        <text className="pd-axis-label" x={x} y={bubbleChart.H - 6} textAnchor="middle">{t}d</text>
                                                    </g>
                                                );
                                            })}
                                            {/* Avg duration marker */}
                                            {stats.avgDuration > 0 && stats.avgDuration <= bubbleChart.xMax && (() => {
                                                const ax = bubbleChart.X0 + (stats.avgDuration / bubbleChart.xMax) * (bubbleChart.X1 - bubbleChart.X0);
                                                return (
                                                    <g>
                                                        <line x1={ax} y1={bubbleChart.TOP} x2={ax} y2={bubbleChart.H - 22} stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="5 4" />
                                                        <text x={ax + 5} y={bubbleChart.TOP + 10} fill="#8b5cf6" fontSize="10" fontWeight="700">avg {stats.avgDuration}d</text>
                                                    </g>
                                                );
                                            })()}
                                            {/* Bubbles */}
                                            {bubbleChart.bubbles.map((b, i) => (
                                                <circle
                                                    key={b.id}
                                                    className="pd-bubble"
                                                    cx={b.x} cy={b.y} r={b.r}
                                                    fill={b.color} fillOpacity="0.6"
                                                    stroke={b.color} strokeWidth="2"
                                                    style={{ animationDelay: `${Math.min(i, 30) * 0.02}s` }}
                                                    onMouseEnter={() => setHoverBubble(b)}
                                                    onClick={() => setQuickView(b.project)}
                                                />
                                            ))}
                                        </svg>
                                        {hoverBubble && (
                                            <div
                                                className="pd-bubble-tip"
                                                style={{
                                                    left: `${(hoverBubble.x / bubbleChart.W) * 100}%`,
                                                    top: `${((hoverBubble.y - hoverBubble.r) / bubbleChart.H) * 100}%`,
                                                }}
                                            >
                                                <strong>{hoverBubble.project.project_code}</strong>
                                                <span>{hoverBubble.project.project_name}</span>
                                                <span>{hoverBubble.dur} days · {hoverBubble.project.project_status}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="pd-bubble-summary">
                                        {bubbleChart.laneAvg.map((l) => (
                                            <div key={l.label} style={{ '--c': statusColors[l.label] }}>
                                                <strong>{l.avg}<small style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}> days avg</small></strong>
                                                <span>{l.label} · {l.count} project(s)</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : <div className="pd-empty"><Layers size={28} /><span>No duration data</span></div>}
                        </div>

                        <div className="pd-panel" style={{ animationDelay: '0.5s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon"><Filter size={15} /></div>
                                Project Pipeline
                            </div>
                            <div className="pd-funnel">
                                {funnelData.map((f, i) => {
                                    const pct = stats.total > 0 ? (f.value / stats.total) * 100 : 0;
                                    return (
                                        <div className="pd-funnel-step" key={f.label}>
                                            <span className="pd-funnel-label">{f.label}</span>
                                            <div className="pd-funnel-track">
                                                <div className="pd-funnel-fill" style={{ width: `${pct}%`, background: f.color, animationDelay: `${0.06 * i}s` }} />
                                            </div>
                                            <span className="pd-funnel-count">{f.value}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* ============== ROW 3: RESPONSIBLE + RECENT + DEADLINES ============== */}
                    <div className="pd-row pd-row-bottom">
                        <div className="pd-panel" style={{ animationDelay: '0.55s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon"><Users size={15} /></div>
                                Top Responsible Persons
                            </div>
                            {responsibleBreakdown.entries.length > 0 ? (
                                <div className="pd-hbar-list">
                                    {responsibleBreakdown.entries.map((r, i) => (
                                        <div key={r.label}>
                                            <div className="pd-hbar-head">
                                                <span className="pd-hbar-name">{r.label}</span>
                                                <span className="pd-hbar-count">{r.count}</span>
                                            </div>
                                            <div className="pd-hbar-track">
                                                <div className="pd-hbar-fill" style={{ width: `${(r.count / responsibleBreakdown.max) * 100}%`, animationDelay: `${i * 0.07}s` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="pd-empty"><Users size={28} /><span>No data yet</span></div>}
                        </div>

                        <div className="pd-panel" style={{ animationDelay: '0.6s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon"><Clock size={15} /></div>
                                Recent Activity
                            </div>
                            {recentActivity.length > 0 ? recentActivity.map((p) => (
                                <div className="pd-list-item" key={p.id} onClick={() => setQuickView(p)}>
                                    <div className="pd-list-body">
                                        <div className="pd-list-main">{p.project_code} • {p.project_name}</div>
                                        <div className="pd-list-sub"><Building2 size={11} />{p.client_detail || '-'}</div>
                                    </div>
                                    <span className="pd-date-chip">{formattedDateLong(p.project_confirmation_date)}</span>
                                </div>
                            )) : <div className="pd-empty"><Clock size={28} /><span>No recent activity</span></div>}
                        </div>

                        <div className="pd-panel" style={{ animationDelay: '0.65s' }}>
                            <div className="pd-panel-title">
                                <div className="pd-panel-title-icon" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', color: '#b45309' }}>
                                    <AlertCircle size={15} />
                                </div>
                                Upcoming Deadlines
                            </div>
                            {upcomingDeadlines.length > 0 ? upcomingDeadlines.map((p) => (
                                <div className="pd-list-item" key={p.id} onClick={() => setQuickView(p)}>
                                    <div className="pd-list-body">
                                        <div className="pd-list-main">{p.project_code} • {p.project_name}</div>
                                        <div className="pd-list-sub"><Users size={11} />{p.responsible_person || '-'}</div>
                                    </div>
                                    <span className={`pd-deadline-badge ${p._overdue ? 'overdue' : p._daysLeft === 0 ? 'today' : 'upcoming'}`}>
                                        {p._overdue ? `Overdue ${Math.abs(p._daysLeft)}d` : p._daysLeft === 0 ? 'Due today' : `${p._daysLeft}d left`}
                                    </span>
                                </div>
                            )) : <div className="pd-empty"><CheckCircle2 size={28} /><span>No upcoming deadlines</span></div>}
                        </div>
                    </div>
                </>
            )}

            {/* ============== STAT DETAIL MODAL ============== */}
            <Modal show={!!activeStatModal} onHide={closeStatModal} dialogClassName="pd-modal model-width80vw" size="xl" centered>
                <div style={{ '--h1': activeConfig?.accent, '--h2': activeConfig?.accent2 }}>
                    <div className="pd-modal-head">
                        <div className="pd-modal-head-left">
                            <div className="pd-modal-head-icon">{ActiveIcon && <ActiveIcon size={22} />}</div>
                            <div>
                                <h5>{activeConfig?.title}</h5>
                                <p>{statModalData.length} project(s) found</p>
                            </div>
                        </div>
                        <button className="pd-modal-close" onClick={closeStatModal} aria-label="Close"><X size={18} /></button>
                    </div>
                    <div className="pd-modal-bodyx">
                        <div className="pd-modal-chips">
                            <div className="pd-modal-chip"><strong>{statModalData.length}</strong>Projects</div>
                            <div className="pd-modal-chip"><strong>{modalAvgDuration}</strong>Avg days</div>
                        </div>
                        <div className="pd-modal-search">
                            <Search size={15} color="#94a3b8" />
                            <input placeholder="Search code / name / client / responsible..." value={modalSearch} onChange={(e) => setModalSearch(e.target.value)} />
                            {modalSearch && (
                                <X size={15} color="#94a3b8" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => setModalSearch('')} />
                            )}
                        </div>
                        <div className="pd-modal-table-wrap">
                            <div className="pd-modal-scroll">
                                <table className="pd-modal-table">
                                    <thead>
                                        <tr>
                                            <th>Project Code</th><th>Project Name</th><th>Client</th><th>Status</th>
                                            <th>Responsible</th><th>Confirmation Date</th><th>Duration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {statModalData.map((p) => {
                                            const dur = Number(p.total_duration) || 0;
                                            const maxDur = Math.max(1, ...statModalData.map((x) => Number(x.total_duration) || 0));
                                            return (
                                                <tr key={p.id} onClick={() => setQuickView(p)}>
                                                    <td className="pd-code">{p.project_code}</td>
                                                    <td>{p.project_name}</td>
                                                    <td>{p.client_detail || '-'}</td>
                                                    <td><StatusPill status={p.project_status} /></td>
                                                    <td>{p.responsible_person || '-'}</td>
                                                    <td>{p.project_confirmation_date ? formattedDateLong(p.project_confirmation_date) : '-'}</td>
                                                    <td>
                                                        {dur > 0 ? (
                                                            <div className="pd-dur-cell">
                                                                <span>{dur}d</span>
                                                                <div className="pd-dur-bar"><i style={{ width: `${(dur / maxDur) * 100}%` }} /></div>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {statModalData.length === 0 && (
                                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '36px', color: '#9ca3af', cursor: 'default' }}>No projects match this filter.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ============== QUICK VIEW MODAL ============== */}
            <Modal show={!!quickView} onHide={() => setQuickView(null)} dialogClassName="pd-modal" size="md" centered>
                <div style={{ '--h1': statusColors[quickView?.project_status] || '#6366f1', '--h2': '#6366f1' }}>
                    <div className="pd-modal-head">
                        <div className="pd-modal-head-left">
                            <div className="pd-modal-head-icon"><FlaskConical size={22} /></div>
                            <div>
                                <h5>{quickView?.project_code}</h5>
                                <p>{quickView?.project_name}</p>
                            </div>
                        </div>
                        <button className="pd-modal-close" onClick={() => setQuickView(null)} aria-label="Close"><X size={18} /></button>
                    </div>
                    <div className="pd-modal-bodyx">
                        {quickView && (
                            <>
                                <div className="pd-qv-grid">
                                    <div className="pd-qv-tile"><label><Info size={12} />Status</label><div><StatusPill status={quickView.project_status} /></div></div>
                                    <div className="pd-qv-tile"><label><Timer size={12} />Total Duration</label><div>{quickView.total_duration ? `${quickView.total_duration} days` : '-'}</div></div>
                                    <div className="pd-qv-tile full"><label><Building2 size={12} />Client</label><div>{quickView.client_detail || '-'}</div></div>
                                    <div className="pd-qv-tile"><label><User size={12} />Responsible</label><div>{quickView.responsible_person || '-'}</div></div>
                                    <div className="pd-qv-tile"><label><Users size={12} />Testing Person(s)</label><div>{quickView.testing_person || '-'}</div></div>
                                    <div className="pd-qv-tile"><label><Calendar size={12} />Confirmation Date</label><div>{quickView.project_confirmation_date ? formattedDateLong(quickView.project_confirmation_date) : '-'}</div></div>
                                    <div className="pd-qv-tile"><label><CheckCircle2 size={12} />Completion Date</label><div>{quickView.completion_date ? formattedDateLong(quickView.completion_date) : '-'}</div></div>
                                    <div className="pd-qv-tile full"><label><FileText size={12} />Final Report Date</label><div>{quickView.final_report_submission_date ? formattedDateLong(quickView.final_report_submission_date) : '-'}</div></div>
                                </div>
                                {quickView.remark_1 && <div className="pd-qv-remark"><b>Remark 1</b>{quickView.remark_1}</div>}
                                {quickView.remark_2 && <div className="pd-qv-remark"><b>Remark 2</b>{quickView.remark_2}</div>}
                            </>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export { ProjectDashboard };